import React from 'react';
import PropTypes from 'prop-types';

import { translationPropType } from '@eb/i18n';
import { ReCaptcha } from 'react-recaptcha-v3';

import { formValidate } from '@eb/eds-utils';

import PasswordField from '../PasswordField';
import EmailField from '../EmailField';
import StaticEmailField from '../StaticEmailField';
import CaptchaField from '../CaptchaField';
import FormSubmitButton from '../FormSubmitButton';

import SoftAccountSection from '../../connectedComponents/SoftAccountSection';
import ForgotPasswordModal from '../../connectedComponents/ForgotPasswordModal';

import { newLoginValidate as validate } from '../utils';
import { LOG_IN_FORM } from '../../constants';

import { APPLE_AUTHENTICATION_DATA_PROPTYPES } from '../../constants/proptypes';

const CAPTCHA_POLL_RATE = 100;
const CAPTCHA_RETRY_RATE = 110000;
const CAPTCHA_MAX_WAIT_TIME = 5000;

class LoginForm extends React.Component {
    static propTypes = {
        isCheckoutAuthenticationFlow: PropTypes.bool,
        handleSubmit: PropTypes.func.isRequired,
        buttonCTA: translationPropType,
        handleSoftAccount: PropTypes.func,
        handleClickViewPassword: PropTypes.func,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        trackOnPageLoad: PropTypes.func,
        isSubmitDisabled: PropTypes.bool,
        socialAuthenticationData: PropTypes.shape({
            apple: APPLE_AUTHENTICATION_DATA_PROPTYPES.isRequired,
        }),

        /* Props Provided Via ReduxForm */
        submitFailed: PropTypes.bool,
        submitSucceeded: PropTypes.bool,
        submitting: PropTypes.bool,
    };

    state = {
        enableCaptchaV3: false,
        captchaToken: null,
    };

    componentDidMount() {
        const { trackOnPageLoad } = this.props;

        if (trackOnPageLoad) {
            trackOnPageLoad();
        }
    }

    componentWillReceiveProps({ submitting, submitFailed, error }) {
        const { showNotification, clearNotification } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;
        clearNotification();

        if (this._submitIfCaptchaTimeout) {
            clearTimeout(this._submitIfCaptchaTimeout);
            this._submitIfCaptchaTimeout = null;
        }

        if (this._handleCaptchaV3Timeout) {
            clearTimeout(this._handleCaptchaV3Timeout);
            this._handleCaptchaV3Timeout = null;
        }
    }

    _handleSoftAccount = () => {
        const { handleSoftAccount, clearNotification, resetForm } = this.props;
        clearNotification();
        handleSoftAccount({});
        if (resetForm) {
            resetForm(LOG_IN_FORM);
        }
    };

    _handleDelayedSubmit = (event) => {
        event.preventDefault();

        this.setState({
            enableCaptchaV3: true,
        });

        this.captchaStartTime = Date.now();

        const submitIfCaptcha = () => {
            const currentTime = Date.now();

            if (
                this.state.captchaToken ||
                this.captchaStartTime + CAPTCHA_MAX_WAIT_TIME < currentTime ||
                this.props.disableRecaptcha3
            ) {
                this.props.handleSubmit();
            } else {
                this._submitIfCaptchaTimeout = setTimeout(
                    submitIfCaptcha,
                    CAPTCHA_POLL_RATE,
                );
            }
        };

        submitIfCaptcha();
    };

    _handleCaptchaV3 = (captchaToken) => {
        this.setState({
            enableCaptchaV3: false,
            captchaToken,
        });

        this.props.change('recaptchaV3ResponseToken', captchaToken);

        //The recaptcha token expires after two minutes
        this._handleCaptchaV3Timeout = setTimeout(() => {
            this.setState({ enableCaptchaV3: true });
        }, CAPTCHA_RETRY_RATE);
    };

    _handlePasswordInput = () => {
        this.setState({
            enableCaptchaV3: true,
        });
    };

    _handleLookupUser = (email) => {
        const { onLookupUser } = this.props;

        onLookupUser && onLookupUser({ email });
    };

    _handleClearNotification = () => {
        const { clearAuthNotification, clearNotification } = this.props;

        clearNotification && clearNotification();
        clearAuthNotification && clearAuthNotification();
    };

    //When user clicks on view password
    _handleOnIconClick = () => {
        const { handleClickViewPassword } = this.props;
        if (handleClickViewPassword) {
            handleClickViewPassword();
        }
    };

    render() {
        const {
            submitting,
            buttonCTA,
            submitFailed,
            showCaptcha,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            disableRecaptcha3,
            isCheckoutAuthenticationFlow,
            email,
            handleEmailEdit,
            isSoftAccount,
        } = this.props;

        const { enableCaptchaV3 } = this.state;

        const isSubmitDisabled = submitting;

        let forgotPasswordLink;
        let softAccountModal;
        let emailField = (
            <EmailField
                submitFailed={submitFailed}
                isStatic={true}
                onBlur={this._handleLookupUser}
                autoFocus={true}
            />
        );

        if (!isCheckoutAuthenticationFlow) {
            forgotPasswordLink = (
                <ForgotPasswordModal dataTestId="signin-forgot-password" />
            );
        }

        if (isSoftAccount) {
            softAccountModal = (
                <SoftAccountSection
                    handleSoftAccount={this._handleSoftAccount}
                />
            );
        }

        if (email) {
            emailField = (
                <StaticEmailField
                    email={email}
                    onEmailEdit={handleEmailEdit}
                    clearNotification={this._handleClearNotification}
                    autoFocus={true}
                />
            );
        }

        return (
            <div className="eds-fx--fade-in eds-fx--delay-2">
                {softAccountModal}
                <form
                    onSubmit={this._handleDelayedSubmit}
                    noValidate="novalidate"
                >
                    {emailField}
                    <PasswordField
                        isLogin={true}
                        submitFailed={submitFailed}
                        onChange={this._handlePasswordInput}
                        isStatic={true}
                        onIconClick={this._handleOnIconClick}
                        autoFocus={false}
                    />

                    <div className="eds-l-pad-bot-4">
                        <CaptchaField
                            showCaptcha={showCaptcha}
                            recaptcha2PublicKey={recaptcha2PublicKey}
                            submitFailed={submitFailed}
                        />

                        {enableCaptchaV3 && !disableRecaptcha3 && (
                            <ReCaptcha
                                sitekey={recaptcha3PublicKey}
                                action="login"
                                verifyCallback={this._handleCaptchaV3}
                            />
                        )}
                    </div>

                    <div className="eds-l-pad-bot-4">
                        <FormSubmitButton
                            submitDisabled={isSubmitDisabled}
                            dataAutomation="signin-submit-button"
                            buttonCTA={buttonCTA}
                        />
                    </div>
                </form>

                <div className="eds-l-pad-bot-4">{forgotPasswordLink}</div>
            </div>
        );
    }
}

export default formValidate({
    validate,
    form: LOG_IN_FORM,
    touchOnBlur: false,
})(LoginForm);
